import { t } from "@lingui/macro";
import { ResponsiveValue } from "@shopify/restyle";
import React from "react";
import { Image } from "react-native";
import Svg, { Rect } from "react-native-svg";

import { getFaceThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { WhooshThemeColors } from "../../../theme/restyle/theme";
import { PersonFilledIcon } from "../../icons_v2/general";
import { Box, Text, WhooshTextProps } from "../../primitive";
import { Stripes } from "../Stripes";

export type AvatarType =
  | "primary"
  | "secondary"
  | "member"
  | "guest"
  | "caddie"
  | "staff"
  | "customer"
  | "unknownCustomer"
  | "unknownGuest"
  | "newGuest"
  | "memberHold"
  | "unknownMemberHold"
  | "empty"
  | "A"
  | "B"
  | "C"
  | "D"
  | "E"
  | "F";

export type AvatarSize =
  | "xs"
  | "tiny"
  | "sm"
  | "small"
  | "md"
  | "medium"
  | "mdlg"
  | "lg"
  | "large"
  | "xl"
  | "extraLarge"
  | "2xl";

type iAvatarProps = {
  profileImagePublicId?: string;
  firstName?: string | null;
  lastName?: string | null;
  type?: AvatarType;
  size?: AvatarSize;
  testID?: string;
  useIcon?: boolean;
};

export const Avatar = ({
  profileImagePublicId,
  firstName,
  lastName,
  type = "member",
  size = "md",
  testID,
  useIcon,
}: iAvatarProps) => {
  const { width, height, borderRadius, nameVariant } = getSizes(size);
  const cornerRadius =
    typeof borderRadius === "number" ? borderRadius : undefined;

  const initials =
    firstName && lastName
      ? `${firstName[0]}${lastName[0]}`.toUpperCase()
      : undefined;

  const fullName =
    firstName && lastName ? `${firstName} ${lastName}` : undefined;

  // Empty avatar is a SVG with a rectangle with a dashed stroke
  if (type === "empty") {
    return (
      <Svg height={height} width={width} testID={testID}>
        <Rect
          x={2}
          y={2}
          width={width - 4}
          height={height - 4}
          stroke="#E1E5E5"
          strokeWidth={1.5}
          strokeDasharray="4,4"
          strokeLinejoin="miter"
          fill="#F7F7F7"
          rx={cornerRadius ?? Math.min(width, height) * 0.05}
          ry={cornerRadius ?? Math.min(width, height) * 0.05}
          strokeLinecap="round"
        />
      </Svg>
    );
  }

  return (
    <Box
      testID={testID}
      width={width}
      height={height}
      borderRadius={borderRadius}
      bg={convertTypeToBGColor(type)}
      overflow="hidden"
      borderStyle="solid"
    >
      <>
        {!!profileImagePublicId ? (
          <Image
            accessibilityLabel={fullName ?? t`Patron Avatar`}
            alt={fullName ?? t`Patron Avatar`}
            testID={`${testID}-image`}
            source={{
              uri: getFaceThumbnail(profileImagePublicId)?.toURL(),
            }}
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          <Box
            width="100%"
            height="100%"
            alignItems="center"
            justifyContent="center"
          >
            {!firstName || !lastName ? (
              useIcon ? (
                <PersonFilledIcon
                  color={convertTypeToTextColor(type)}
                  testID={`${testID}-icon`}
                />
              ) : (
                <Stripes
                  type={type}
                  style={{ position: "absolute" }}
                  testID={`${testID}-stripes`}
                />
              )
            ) : (
              <Text
                variant={nameVariant}
                color={convertTypeToTextColor(type)}
                testID={`${testID}-initials`}
              >
                {initials}
              </Text>
            )}
          </Box>
        )}
      </>
    </Box>
  );
};

const convertTypeToBGColor = (
  type: AvatarType = "member"
): WhooshThemeColors => {
  switch (type) {
    case "guest":
    case "unknownGuest":
    case "newGuest":
      return "surface-main-secondary-subtlest";
    case "A":
    case "B":
    case "C":
    case "D":
    case "E":
    case "F":
      return `surface-user-selectable-color${type}-subtlest`;

    case "primary":
    case "staff":
    case "empty":
    case "secondary":
      return "surface-secondary";
    case "member":
    case "customer":
    default:
      return "surface-main-primary-subtlest";
  }
};

const convertTypeToTextColor = (
  type: AvatarType = "member"
): WhooshThemeColors => {
  switch (type) {
    case "guest":
    case "unknownGuest":
    case "newGuest":
    case "empty":
    case "primary":
    case "secondary":
      return "text-primary";
    case "A":
    case "B":
    case "C":
    case "D":
    case "E":
    case "F":
      return `text-user-selectable-color${type}-inverse`;
    case "member":
    case "customer":
    default:
      return "text-main-primary-inverse";
  }
};

const getSizes = (
  size: AvatarSize = "md"
): {
  width: number;
  height: number;
  nameVariant: WhooshTextProps["variant"];
  borderRadius:
    | ResponsiveValue<
        "0" | "0.5" | "1" | "2" | "4" | "6" | "8",
        {
          base: number;
          xs: number;
          sm: number;
          md: number;
          agendaSplit: number;
          eventCardColumn: number;
          lg: number;
          xl: number;
        }
      >
    | undefined;
} => {
  switch (size) {
    case "xs":
    case "tiny":
      return {
        width: 24,
        height: 24,
        borderRadius: "1",
        nameVariant: "mainSmallSemiBold",
      };
    case "sm":
    case "small":
      return {
        width: 40,
        height: 40,
        borderRadius: "1",
        nameVariant: "mainHeading",
      };
    case "md":
    case "medium":
      return {
        width: 64,
        height: 64,
        borderRadius: "2",
        nameVariant: "serifTitle2",
      };
    case "mdlg":
      return {
        width: 86,
        height: 86,
        borderRadius: "2",
        nameVariant: "serifTitle2",
      };
    case "lg":
    case "large":
      return {
        width: 96,
        height: 96,
        borderRadius: "2",
        nameVariant: "serifTitle1",
      };
    case "xl":
    case "extraLarge":
      return {
        width: 120,
        height: 120,
        borderRadius: "2",
        nameVariant: "serifTitle1",
      };
    case "2xl":
      return {
        width: 176,
        height: 176,
        borderRadius: "4",
        nameVariant: "serifTitle1",
      };
  }
};

export const getAvatarTypeFromPatronType = (patronType: string): AvatarType => {
  switch (patronType) {
    case "Member":
    case "Member Hold":
    case "MEMBER":
      return "member";

    case "Guest":
    case "New Guest":
    case "Unknown Guest":
    case "GUEST":
      return "guest";

    case "Customer":
    case "Unknown Customer":
    case "CUSTOMER":
      return "customer";

    default:
      return "primary";
  }
};
